import { CSSResultGroup, html, LitElement, PropertyValues } from 'lit'
import { customElement, property, queryAssignedElements } from 'lit/decorators.js'
import { style } from './selectable-group.style.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { classMap } from 'lit/directives/class-map.js'
import { groupJustifyOptionsType } from 'elements/group/group.js'
import { CSESelectable } from 'elements/selectable/selectable.js'
import { SingleSelectableStrategy, MultipleSelectableStrategy, SelectableStrategy } from './selectable-group-strategies.js'

@customElement('cse-selectable-group')
export class CSESelectableGroup extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ type: Boolean })
  inline = false

  @property({ type: String })
  justify: groupJustifyOptionsType[number] = 'start'

  @property({ type: Array, attribute: 'selected-indices' })
  selectedIndices: number[] = []

  @property({ type: Boolean })
  multiple = false

  @queryAssignedElements({ flatten: true })
  private selectables!: CSESelectable[]

  private strategy: SelectableStrategy | null = null

  private clickListeners = new Map<CSESelectable, () => void>()

  override firstUpdated() {
    this.setupStrategy()
    this.setupListeners()
  }

  override updated(changedProps: PropertyValues) {
    if (changedProps.has('multiple')) {
      this.setupStrategy()
    }

    if (changedProps.has('selectedIndices')) {
      this.setupListeners()
      this.strategy?.updateState()
    }
  }

  private handleSlotChange() {
    this.setupListeners()
    this.strategy?.updateState()
  }

  private setupStrategy() {
    this.strategy = this.multiple
      ? new MultipleSelectableStrategy(
        () => this.selectables,
        () => this.selectedIndices,
        (indices: number[]) => { this.selectedIndices = [...indices] },
        (event: CustomEvent) => this.dispatchEvent(event)
      )
      : new SingleSelectableStrategy(
        () => this.selectables,
        () => this.selectedIndices,
        (indices: number[]) => { this.selectedIndices = [...indices] },
        (event: CustomEvent) => this.dispatchEvent(event)
      )

    this.strategy.updateState()
  }

  private setupListeners() {
    if (!this.selectables) { return }

    this.clickListeners.forEach((handler, item) => {
      item.removeEventListener('click', handler)
    })
    this.clickListeners.clear()

    this.selectables.forEach((item, index) => {
      const handler = () => this.strategy?.handleClick(index)
      item.addEventListener('click', handler)
      this.clickListeners.set(item, handler)
    })
  }

  override render() {
    return html`
      <div
        class="${classMap({
          'cse-selectable-group': true,
          'space-between': this.justify === 'space-between',
          'space-evenly': this.justify === 'space-evenly',
          'start': this.justify === 'start',
          'center': this.justify === 'center',
          'end': this.justify === 'end',
          inline: this.inline,
        })}"
      >
        <slot @slotchange=${this.handleSlotChange}></slot>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cse-selectable-group': CSESelectableGroup
  }
}
